/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import "./App.scss";
import { LiveAPIProvider } from "./contexts/LiveAPIContext";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { AdminDashboard } from "./components/admin/AdminDashboard";
import { AdminSOPDetails } from "./components/admin/AdminSOPDetails";
import { AdminReports } from "./components/admin/AdminReports";
import { Register } from "./components/auth/Register";
import SidePanel from "./components/side-panel/SidePanel";
import { Altair } from "./components/altair/Altair";
import { Jarvis } from "./components/jarvis/Jarvis";
import ControlTray from "./components/control-tray/ControlTray";
import VideoPlayground from "./components/video-playground/VideoPlayground";
// import VideoCaptioning from "./components/video-captioning/VideoCaptioning";
import TextChat from "./components/text-chat/TextChat";
import VideoUnderstanding from "./components/video-understanding/VideoUnderstanding";
import Lab from "./components/lab/Lab";
import cn from "classnames";
import { LiveClientOptions } from "./types";

const API_KEY = process.env.REACT_APP_GEMINI_API_KEY as string;
if (typeof API_KEY !== "string") {
  throw new Error("set REACT_APP_GEMINI_API_KEY in .env");
}

const apiOptions: LiveClientOptions = {
  apiKey: API_KEY,
};

// type AppMode = 'altair' | 'jarvis' | 'video-playground' | 'video-captioning' | 'text-chat' | 'video-understanding';
type AppMode = 'jarvis';

function JarvisApp() {
  // this video reference is used for displaying the active stream, whether that is the webcam or screen capture
  // feel free to style as you see fit
  const videoRef = useRef<HTMLVideoElement>(null);
  // either the screen capture, the video or null, if null we hide it
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);

  return (
    <LiveAPIProvider options={apiOptions}>
      <div className="streaming-console">
        <SidePanel />
        <main>
          <div className="main-app-area jarvis-mode">
            <Jarvis videoRef={videoRef} videoStream={videoStream} />
          </div>

          <ControlTray
            videoRef={videoRef}
            supportsVideo={true}
            onVideoStreamChange={setVideoStream}
            enableEditingSettings={true}
          >
            {/* put your own buttons here */}
          </ControlTray>
        </main>
      </div>
    </LiveAPIProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/reports" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminReports />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/admin/sop/:id" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminSOPDetails />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/jarvis" 
              element={
                <ProtectedRoute requiredRole="technician">
                  <JarvisApp />
                </ProtectedRoute>
              } 
            />
            <Route path="/lab" element={<Lab />} />
            <Route path="/register" element={<Register />} />
            <Route path="/" element={<Navigate to="/jarvis" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
